import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import Loading from 'flow/components/ui-components/Loading';
import Icon from '../ui-components/Icon';
import { Sound } from 'flow/types/Sound';

import Button from '../ui-components/Button';
import { PbxParams } from 'flow/types/Params';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { homeActionUrlPath } from 'flow/utils';
import Page from 'flow/components/Setting/Page';
import IconButton from '../ui-components/IconButton';
import useAppContext from 'flow/hooks/useAppContext';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { deleteSound, fetchDefaultSounds } from 'flow/actions/sounds-actions';
import { MiniAudioPlayer } from 'flow/components/AudioPlayers';
import { useModal } from 'flow/contexts/ModalProvider';

const PbxWelcomeMessageSounds = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { entityKey } = useParams<PbxParams>();
    const { openModal } = useModal();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    useEffect(() => {
        dispatch(fetchDefaultSounds());
    }, [dispatch]);

    const sounds = useAppSelector((state) => state.sounds);
    const userSounds = sounds?.get('sounds');

    if (!userSounds) {
        return <Loading />;
    }
    const customSounds = userSounds
        ? userSounds.filter(
              (soundDTO) => soundDTO.get('privateSound') === false,
          )
        : null;

    return (
        <Page
            header="pbx.user_sounds"
            onClickBack={() =>
                history.push(
                    generatePath(
                        routes['pbx.queues.entityKey.settings.welcomemessage'],
                        {
                            entityKey,
                        },
                    ),
                )
            }
        >
            <>
                <div className="mb-6 px-2">
                    <Button
                        className="w-full"
                        color="ghost"
                        onClick={() => {
                            openModal('createSound', {
                                extensionKey: ownExtensionKey,
                            });
                        }}
                    >
                        {i18next.t('Recorder.new.sound')}
                    </Button>
                </div>

                {customSounds?.map((soundDTO: TypedMap<Sound>) => {
                    if (!soundDTO.has('playbackURL')) {
                        return null;
                    }

                    const playbackUrl = homeActionUrlPath(
                        `/${soundDTO.get('playbackURL')}`,
                    );

                    return (
                        <div
                            key={soundDTO.get('key')}
                            className="flex h-13 grow items-center justify-between gap-x-2 rounded-sm p-2 hover:bg-gray-100"
                        >
                            <div className="flex items-center gap-x-2">
                                <div>
                                    {playbackUrl ? (
                                        <MiniAudioPlayer
                                            url={playbackUrl}
                                            uniqueId={`user-sound-key${soundDTO.get(
                                                'key',
                                            )}`}
                                        />
                                    ) : null}
                                </div>

                                <div className="flex items-center text-sm text-gray-800">
                                    {soundDTO.get('name') || ''}
                                </div>
                            </div>

                            <div className="flex items-center gap-x-1">
                                <IconButton
                                    aria-label={i18next.t(
                                        'Tooltip.Nav.Title.Edit',
                                    )}
                                    onClick={() =>
                                        openModal('editSound', {
                                            soundKey: soundDTO.get('key'),
                                        })
                                    }
                                >
                                    <Icon iconKey="edit" />
                                </IconButton>

                                <IconButton
                                    aria-label={i18next.t('Delete.Button.Text')}
                                    onClick={() => {
                                        /* No onInUse by design: falls back to the
                                         * in-use snackbar, not the itemized modal. */
                                        dispatch(
                                            deleteSound(soundDTO.get('key')),
                                        );
                                    }}
                                >
                                    <Icon iconKey="delete" />
                                </IconButton>
                            </div>
                        </div>
                    );
                })}
            </>
        </Page>
    );
};

export default PbxWelcomeMessageSounds;
